import { Select } from '@mantine/core'
import type {
  TValuesCurrentBlocks,
  TValuesCurrentHeatmapPoints,
  TValuesCurrentPoints,
  TValuesCurrentProperties,
} from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { useMemo } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { getPrimaryValueGroup } from 'stores/selectedValueGroupsStore/getPrimaryValueGroup'
import type { TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { usePrimaryValueGroup } from 'utils/usePrimaryValueGroup'

const updateMapVisuals = (newValueType: TValuesCurrentPoints & TValuesCurrentHeatmapPoints) => {
  const primaryValueGroup = getPrimaryValueGroup() as TValueGroup

  mapControlsStore.setState((s) => ({
    ...s,
    mapVisualValueGroup: {
      ...s.mapVisualValueGroup,
      [MAP_VISUAL.POINT]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.POINT],
        [primaryValueGroup]: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.POINT][primaryValueGroup] ?? { mItem: 'M_InBlockPoint' }),
          valueType: newValueType,
        },
      },
      [MAP_VISUAL.PROPERTY]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.PROPERTY],
        [primaryValueGroup]: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.PROPERTY][primaryValueGroup] ?? { mItem: 'M_Property' }),
          valueType: newValueType.replace('Sum_', 'SumMinMedianMax_') as TValuesCurrentProperties,
        },
      },
      [MAP_VISUAL.SCD]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.SCD],
        [primaryValueGroup]: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.SCD][primaryValueGroup] ?? { mItem: 'M_SCD' }),
          valueType: newValueType,
        },
      },
      [MAP_VISUAL.BLOCK]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.BLOCK],
        [primaryValueGroup]: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.BLOCK][primaryValueGroup] ?? { mItem: 'M_SCD' }),
          valueType: newValueType.replace('Sum_', 'SumMinMedianMax_') as TValuesCurrentBlocks,
        },
      },
      [MAP_VISUAL.REGION]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.REGION],
        [primaryValueGroup]: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.REGION][primaryValueGroup] ?? { mItem: 'M_Region' }),
          valueType: newValueType,
        },
      },
    },
  }))
}

export const InsectDegreeDaysSecondaryDropdown = () => {
  const primaryValueGroup = usePrimaryValueGroup()

  const { insectId, value } = mapControlsStore.useSelector((s) => ({
    insectId: !primaryValueGroup ? null : Number(primaryValueGroup.replace('degree_days_insect_id_', '')),
    value: !primaryValueGroup
      ? null
      : s.mapVisualValueGroup?.[MAP_VISUAL.POINT]?.[primaryValueGroup]?.valueType,
  }))

  const isUserSelfRegistered = isUserOnlyAFreeRegionalUser()

  const data: { group: string; items: { value: TValuesCurrentPoints; label: string }[] }[] = useMemo(() => {
    const items: { value: TValuesCurrentPoints; label: string }[] = [
      {
        value: `insectDegreeDays_sinceBiofixSum_insectId${insectId}_degreeDaysC` as TValuesCurrentPoints,
        label: translate.phrases.banyanApp('Current'),
      },
      {
        value:
          `insectDegreeDays7DaysFromNow_sinceBiofixSum_insectId${insectId}_degreeDaysC` as TValuesCurrentPoints,
        label: translate.phrases.banyanApp('{{count}} Days From Now', { count: 7 }),
      },
    ]

    if (!isUserSelfRegistered) {
      items.push({
        value:
          `insectDegreeDays14DaysFromNow_sinceBiofixSum_insectId${insectId}_degreeDaysC` as TValuesCurrentPoints,
        label: translate.phrases.banyanApp('{{count}} Days From Now', { count: 14 }),
      })
    }

    return [{ group: translate.phrases.banyanApp('Since Biofix'), items }]
  }, [insectId, value])

  return (
    <Select
      data={data}
      value={value}
      onChange={(newValueType) => {
        if (newValueType) updateMapVisuals(newValueType as TValuesCurrentPoints & TValuesCurrentHeatmapPoints)
      }}
      styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
      comboboxProps={{ withinPortal: true }}
    />
  )
}
